import { useEffect, useRef } from "react";
import { asBackgroundRefresh } from "../api/apiFetch";

/**
 * How often a page refreshes its data, by how long it has been open: often at first, while the person is most likely to
 * be watching for a change, then less often, settling at a slow, steady rate. Each tier applies until the page has been
 * open for `untilMs`.
 */
export const pollingTiers: readonly { untilMs: number; everyMs: number }[] = [
  { untilMs: 60_000, everyMs: 10_000 },
  { untilMs: 5 * 60_000, everyMs: 30_000 },
  { untilMs: Number.POSITIVE_INFINITY, everyMs: 60_000 },
];

/**
 * The wait before the next refresh, for a page that has been open for `elapsedMs`.
 *
 * @param elapsedMs - How long the page has been open (or visible again).
 * @returns The wait in milliseconds.
 */
export function pollingIntervalFor(elapsedMs: number): number {
  return (pollingTiers.find((tier) => elapsedMs < tier.untilMs) ?? pollingTiers[pollingTiers.length - 1]).everyMs;
}

/**
 * The previous value when the next one holds the same data, so a refresh that changes nothing does not re-render the
 * page (or redraw its charts). Pass it to a state setter: `setState((previous) => unchangedOr(previous, next))`.
 *
 * @param previous - The value shown now.
 * @param next - The value just fetched.
 * @returns `previous` when the two serialise the same, otherwise `next`.
 */
export function unchangedOr<T>(previous: T, next: T): T {
  return JSON.stringify(previous) === JSON.stringify(next) ? previous : next;
}

/**
 * Refreshes a page's data on the tiered schedule in {@link pollingTiers}, counted from when the page opened. Refreshes
 * never overlap: the next is scheduled when the last has finished. While the browser tab is hidden nothing is fetched;
 * when it is shown again the page refreshes at once and the schedule starts over, as the person is looking again.
 *
 * The refresh should be quiet: no loading state, and a failure keeps the data already shown (a `401` already makes the
 * session check itself again, see `apiFetch`). Its requests are marked as automatic, so they do not keep the session alive;
 * for that, it must start all of them before its first `await` (see `asBackgroundRefresh`).
 *
 * @param refresh - Fetches the page's data again, quietly. The latest one passed is always the one called.
 * @param enabled - False pauses the schedule (while the first load is under way, say, or while the person is editing);
 * it carries on from where it was when enabled again.
 */
export function usePolling(refresh: () => Promise<void> | void, enabled = true): void {
  const refreshRef = useRef(refresh);
  const enabledRef = useRef(enabled);
  refreshRef.current = refresh;
  enabledRef.current = enabled;

  useEffect(() => {
    let startedAt = Date.now();
    let timer: number | undefined;
    let stopped = false;

    const schedule = (): void => {
      window.clearTimeout(timer);
      if (stopped || document.visibilityState === "hidden") {
        return;
      }

      timer = window.setTimeout(() => void tick(), pollingIntervalFor(Date.now() - startedAt));
    };

    const tick = async (): Promise<void> => {
      if (enabledRef.current) {
        try {
          await asBackgroundRefresh(() => refreshRef.current());
        } catch {
          // A refresh that fails keeps the data shown; the next one tries again.
        }
      }

      schedule();
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === "hidden") {
        window.clearTimeout(timer);
        return;
      }

      startedAt = Date.now();
      void tick();
    };

    schedule();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);
}
